/**
 * V2 Icon system — thin wrapper around lucide-react that enforces
 * consistent sizing, stroke-width and colour inheritance.
 *
 * Usage:
 *   import { Icon } from '@/v2/components/common/Icon';
 *   import { ShieldCheck } from 'lucide-react';
 *   <Icon as={ShieldCheck} size="md" />
 */

import React from 'react';
import type { LucideIcon } from 'lucide-react';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const sizeMap: Record<IconSize, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

interface IconProps {
  as: LucideIcon;
  size?: IconSize | number;
  className?: string;
  strokeWidth?: number;
  'aria-hidden'?: boolean;
}

export const Icon: React.FC<IconProps> = ({
  as: LucideComponent,
  size = 'md',
  className = '',
  strokeWidth = 1.8,
  'aria-hidden': ariaHidden = true,
}) => {
  const px = typeof size === 'number' ? size : sizeMap[size];
  return (
    <LucideComponent
      width={px}
      height={px}
      strokeWidth={strokeWidth}
      className={className}
      aria-hidden={ariaHidden}
    />
  );
};

// ─── Re-export every icon used in the V2 system ───────────────────────────────
// Importing from here keeps individual files lean and makes global icon swaps easy.

export {
  // Navigation / UI chrome
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  ExternalLink,

  // Trust & safety
  ShieldCheck,
  Shield,
  Lock,
  Eye,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,

  // Social / users
  Users,
  UserCheck,
  Heart,
  MessageCircle,
  MessageSquare,
  Mail,

  // Commerce / listings
  Tag,
  Bookmark,
  Star,
  DollarSign,
  CreditCard,
  Wallet,

  // Media / content
  Camera,
  Image,
  FileText,
  RefreshCw,

  // Logistics
  Package,
  Box,
  Truck,
  CalendarDays,
  CalendarCheck,
  Clock,
  MapPin,

  // Actions / process
  Search,
  CheckSquare,
  Wrench,
  Home,
  Laptop,
  Leaf,
  Tent,
  Dumbbell,
  Baby,
  Car,
  PartyPopper,
  LayoutGrid,

  // Misc
  Building2,
  Umbrella,
  KeyRound,
  Sparkles,
  BadgeCheck,
  Zap,
} from 'lucide-react';
